import { _ as _async_to_generator } from "@swc/helpers/_/_async_to_generator";
import { _ as _sliced_to_array } from "@swc/helpers/_/_sliced_to_array";
import { _ as _ts_generator } from "@swc/helpers/_/_ts_generator";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Group, Kbd, Menu, Stack, Text, TopBar, useHotkeys } from '@components/mantine';
import { Guard } from '@components/security';
import { getPlatform } from '@core/registry';
import { useQueryClient } from '@tanstack/react-query';
import { doLogout } from '@core/authentication';
import { LicenseHelper } from '@core/license';
import { Link } from '@core/routes';
import { updateUser, UserSelectors, useUserModel } from '@core/user';
import { IconBug, IconLogout, IconUserCircle } from '@coveord/plasma-react-icons';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Locales } from '../../strings/Locales.js';
import { UserTracking } from '../../tracking/UserTracking.js';
import { DomainProviders, ProviderSvgIcon } from '../../utils/GetDomainProviderIcon.js';
import classes from './UserMenu.module.css';
export var UserMenu = function UserMenu(param) {
    var position = param.position, offset = param.offset, arrowPosition = param.arrowPosition, _param_showInternalSection = param.showInternalSection, showInternalSection = _param_showInternalSection === void 0 ? true : _param_showInternalSection, _param_showUserNotifications = param.showUserNotifications, showUserNotifications = _param_showUserNotifications === void 0 ? false : _param_showUserNotifications, notificationPreferencesPath = param.notificationPreferencesPath;
    var _useUserModel = useUserModel(), displayName = _useUserModel.displayName, email = _useUserModel.email, provider = _useUserModel.provider;
    var _useState = _sliced_to_array(useState(false), 2), opened = _useState[0], setOpened = _useState[1];
    var providerIconHeight = [
        DomainProviders.SAML,
        DomainProviders.EMAIL,
        DomainProviders.ANY
    ].includes(provider) ? 22 : 30;
    useEffect(function() {
        if (opened) {
            UserTracking.openedUserProfileMenu();
        }
    }, [
        opened
    ]);
    return /*#__PURE__*/ _jsxs(Menu, {
        position: position,
        offset: offset,
        arrowPosition: arrowPosition,
        shadow: "md",
        width: 280,
        opened: opened,
        onChange: setOpened,
        classNames: {
            itemLabel: classes.itemLabel
        },
        withinPortal: true,
        keepMounted: true,
        children: [
            /*#__PURE__*/ _jsx(Menu.Target, {
                children: /*#__PURE__*/ _jsx(TopBar.Action, {
                    children: /*#__PURE__*/ _jsx(IconUserCircle, {
                        "aria-label": Locales.format('UserMenu.button.label')
                    })
                })
            }),
            /*#__PURE__*/ _jsxs(Menu.Dropdown, {
                children: [
                    /*#__PURE__*/ _jsx(Menu.Item, {
                        leftSection: /*#__PURE__*/ _jsx(ProviderSvgIcon, {
                            provider: provider,
                            height: providerIconHeight
                        }),
                        disabled: true,
                        opacity: 1,
                        children: /*#__PURE__*/ _jsxs(Stack, {
                            gap: 0,
                            children: [
                                /*#__PURE__*/ _jsx(Text, {
                                    c: "var(--mantine-color-text)",
                                    fw: 500,
                                    truncate: true,
                                    children: displayName
                                }),
                                /*#__PURE__*/ _jsx(Text, {
                                    c: "var(--mantine-color-text)",
                                    truncate: true,
                                    children: email
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsx(Menu.Divider, {}),
                    /*#__PURE__*/ _jsx(Menu.Item, {
                        component: "a",
                        href: Locales.format('systemSecurityDoc'),
                        target: "_blank",
                        children: Locales.format('systemSecurity')
                    }),
                    /*#__PURE__*/ _jsx(Menu.Item, {
                        component: "a",
                        href: Locales.format('termsAndPrivacyDoc'),
                        target: "_blank",
                        children: Locales.format('termsAndPrivacy')
                    }),
                    /*#__PURE__*/ _jsx(Guard, {
                        canRender: function canRender(param) {
                            var user = param.user, license = param.license;
                            return showUserNotifications && user.canViewNotifications && !LicenseHelper.isTrialExpired(license);
                        },
                        children: notificationPreferencesPath ? /*#__PURE__*/ _jsx(Menu.Item, {
                            component: Link,
                            to: notificationPreferencesPath,
                            children: /*#__PURE__*/ _jsx(Text, {
                                children: Locales.format('UserMenu.notificationPreferences.label')
                            })
                        }) : /*#__PURE__*/ _jsx(Menu.Item, {
                            component: Link,
                            to: "/user/notifications",
                            children: /*#__PURE__*/ _jsx(Text, {
                                children: Locales.format('UserMenu.userNotifications.label')
                            })
                        })
                    }),
                    /*#__PURE__*/ _jsx(Menu.Divider, {}),
                    showInternalSection && /*#__PURE__*/ _jsx(InternalUserMenuSection, {}),
                    /*#__PURE__*/ _jsx(Menu.Item, {
                        leftSection: /*#__PURE__*/ _jsx(IconLogout, {
                            height: 16,
                            width: 16
                        }),
                        onClick: doLogout,
                        children: Locales.format('logOut')
                    })
                ]
            })
        ]
    });
};
var InternalUserMenuSection = function InternalUserMenuSection() {
    return /*#__PURE__*/ _jsxs(Guard, {
        canRender: function canRender(param) {
            var user = param.user;
            return user.isInternalUser;
        },
        children: [
            /*#__PURE__*/ _jsx(Menu.Label, {
                children: Locales.format('internal')
            }),
            /*#__PURE__*/ _jsx(ToggleBugReporter, {}),
            /*#__PURE__*/ _jsx(Menu.Divider, {})
        ]
    });
};
var ToggleBugReporter = function ToggleBugReporter() {
    var _user_additionalInformation;
    var queryClient = useQueryClient();
    var dispatch = useDispatch();
    var user = useSelector(UserSelectors.getUser);
    var isFeedbackEnabled = (user === null || user === void 0 ? void 0 : (_user_additionalInformation = user.additionalInformation) === null || _user_additionalInformation === void 0 ? void 0 : _user_additionalInformation.showFeedbackButton) === 'true';
    var onToggleFeedback = function onToggleFeedback() {
        return _async_to_generator(function() {
            var newValue;
            return _ts_generator(this, function(_state) {
                switch(_state.label){
                    case 0:
                        newValue = isFeedbackEnabled ? 'false' : 'true';
                        user.additionalInformation.showFeedbackButton = newValue;
                        return [
                            4,
                            getPlatform().user.update({
                                additionalInformation: user === null || user === void 0 ? void 0 : user.additionalInformation
                            })
                        ];
                    case 1:
                        _state.sent();
                        dispatch(updateUser(user));
                        queryClient.invalidateQueries({
                            queryKey: [
                                'init',
                                'user'
                            ]
                        });
                        return [
                            2
                        ];
                }
            });
        })();
    };
    useHotkeys([
        [
            'alt+b',
            onToggleFeedback
        ]
    ]);
    var label = isFeedbackEnabled ? 'Hide issue reporter' : 'Show issue reporter';
    return /*#__PURE__*/ _jsx(Menu.Item, {
        onClick: onToggleFeedback,
        leftSection: /*#__PURE__*/ _jsx(IconBug, {
            size: 16
        }),
        rightSection: /*#__PURE__*/ _jsxs(Group, {
            wrap: "nowrap",
            gap: 4,
            children: [
                /*#__PURE__*/ _jsx(Kbd, {
                    size: "xs",
                    children: "ALT"
                }),
                "+",
                /*#__PURE__*/ _jsx(Kbd, {
                    size: "xs",
                    children: "B"
                })
            ]
        }),
        children: label
    });
};

//# sourceMappingURL=UserMenu.js.map